import { integer } from '../kernel/primitives.ts';
import {
  recordRefusal,
  wasRefused,
  type CompanionContract,
  type GoalKind,
  type RefusalReason,
} from './contracts.ts';

/**
 * Out-of-combat discussion. The PC proposes a new approach weight for one goal
 * ("stay cautious", "protect me", "press the attack"); the companion's
 * temperament, obligations and boundaries decide whether they accept. There is
 * no command path here: a refused topic stays refused no matter how often the
 * PC repeats it, and preferences change only on acceptance.
 */
export type DiscussionProposal = Readonly<{ goal: GoalKind; requestedWeight: number }>;

export type DiscussionOutcome = Readonly<{
  contract: CompanionContract;
  accepted: boolean;
  /** Always present. Acceptances record 'accepted'; refusals record why. */
  reason: RefusalReason | 'accepted';
}>;

/** How much weight this temperament can stomach for each goal. Integer 0–10. */
function affinity(temperament: Readonly<{ boldness: number; loyalty: number }>, goal: GoalKind): number {
  if (goal === 'engage') return temperament.boldness;
  if (goal === 'guard') return temperament.loyalty;
  return 10 - temperament.boldness;
}

function forbids(contract: Readonly<CompanionContract>, goal: GoalKind): boolean {
  if (contract.obligations.some(obligation => !obligation.against && obligation.forbiddenGoals.includes(goal))) return true;
  return contract.boundaries.some(
    boundary =>
      (boundary.kind === 'refuse-goal' && boundary.goal === goal) ||
      (boundary.kind === 'non-negotiable-topic' && boundary.topic === goal),
  );
}

export function discuss(contract: CompanionContract, proposal: DiscussionProposal): DiscussionOutcome {
  if (!['engage', 'guard', 'hold', 'withdraw'].includes(proposal.goal)) throw new Error('Unknown companion goal');
  if (proposal.requestedWeight !== integer(proposal.requestedWeight) || proposal.requestedWeight < 0 || proposal.requestedWeight > 10) {
    throw new Error('A discussion proposal needs an integer weight from 0 to 10');
  }
  const topic = `approach:${proposal.goal}`;
  if (wasRefused(contract, topic)) {
    return { contract: recordRefusal(contract, topic, 'already-refused'), accepted: false, reason: 'already-refused' };
  }
  if (forbids(contract, proposal.goal)) {
    const reason: RefusalReason = contract.obligations.some(obligation => !obligation.against && obligation.forbiddenGoals.includes(proposal.goal))
      ? 'obligation-forbids'
      : 'boundary-non-negotiable';
    return { contract: recordRefusal(contract, topic, reason), accepted: false, reason };
  }
  if (proposal.requestedWeight > affinity(contract.temperament, proposal.goal) + 2) {
    return { contract: recordRefusal(contract, topic, 'temperament-declines'), accepted: false, reason: 'temperament-declines' };
  }
  const next = structuredClone(contract);
  next.discussionSequence = integer(next.discussionSequence) + 1;
  next.preferences = { ...next.preferences, [proposal.goal]: proposal.requestedWeight };
  next.agreedApproach = proposal.goal;
  return { contract: next, accepted: true, reason: 'accepted' };
}
